import { useContext, useEffect, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { GraphQLOperationFailed } from '../../../../adapter/GraphQLClient';
import ProjectEditingStep, { DraftSteps } from '../../../../entity/ProjectEditingStep';
import {
  ProjectDetailsFragment,
  useGetAvailableDeliveryDueDatesLazyQuery,
  useGetProjectByIdForOptionScreenLazyQuery,
  useGetProjectByIdLazyQuery,
  useGetProjectByIdWithoutPricingDataLazyQuery,
} from '../../../../generated/graphql';
import { ProjectNotFound } from '../../../../service/persistent/ProjectWebApi';
import { context as DependencyContext } from '../../../context/dependency';
import { context as StateContext } from '../../../context/state';
import { ExtendedProject } from '../../../types/ExtendedProject';
import {
  editingProjectAssetFilesStateAtom,
  editingProjectBriefFilesStateAtom,
  editingProjectDeliveryDueDatesAtom,
} from '../atoms/editingProjectStateAtom';
import { projectAtom, projectLoadingAtom } from '../atoms/projectAtom';
import { isProjectInstanceOfProjectDetailsFragment } from './useUpdateProject';

export const useGetProject = () => {
  const { language } = useContext(StateContext);
  const {
    editProjectUsecase: {
      setAvailablePricingMerchandiseOptions,
      setLastAccessedOrganization,
      showFatalErrorMessage,
      getBriefFiles,
      getAssetFiles,
    },
  } = useContext(DependencyContext);

  const [error, setError] = useState<Error | null>(null);
  const [briefFilesState, setBriefFilesState] = useRecoilState(editingProjectBriefFilesStateAtom);
  const [assetFilesState, setAssetFilesState] = useRecoilState(editingProjectAssetFilesStateAtom);
  const setProject = useSetRecoilState(projectAtom);
  const setProjectLoading = useSetRecoilState(projectLoadingAtom);
  const setAvailableDeliveryDueDates = useSetRecoilState(editingProjectDeliveryDueDatesAtom);

  const [getAvailableDeliveryDueDates] = useGetAvailableDeliveryDueDatesLazyQuery({
    fetchPolicy: 'no-cache',
  });

  // それぞれのステップに対するProjectの取得クエリ
  const [getProjectById, { loading }] = useGetProjectByIdLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [
    getProjectByIdForOptionScreen,
    { loading: isProjectForOptionScreenLoading },
  ] = useGetProjectByIdForOptionScreenLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [
    getProjectByIdWithoutPricingData,
    { loading: withoutPricingDataLoading },
  ] = useGetProjectByIdWithoutPricingDataLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const getProject = async ({ projectId, step }: { projectId: string; step?: ProjectEditingStep }) => {
    const fetchProject = async (): Promise<ExtendedProject | void> => {
      try {
        let rawData: any; // TODO: any型を使わない実装を行う

        // TODO: Stepごとに違うLazyQueryを呼ぶ。
        // FIXME: 社内ユーザ向けのstepのformat, due, briefを全て一つのページで表示させる実装を既存ではisFlatという関数から
        //   判別しているが、それをそれぞれのページ内で表示を切り替えるのではなく、社内向けと社外向けとで全く別のページで実装しないと、
        //   format, due, briefのクエリの最適を行うことができない
        // TODO: 命名を変える -> getProjectForSubmittedScreen
        if (step === 'submitted') {
          const response = await getProjectByIdWithoutPricingData({
            variables: {
              id: projectId,
              language,
            },
          });
          rawData = response.data;
        } else if (step === 'option') {
          const response = await getProjectByIdForOptionScreen({
            variables: {
              id: projectId,
              language,
            },
          });
          rawData = response.data;
        } else {
          const response = await getProjectById({
            variables: {
              id: projectId,
              language,
            },
          });
          rawData = response.data;
        }

        if (rawData?.project == null) {
          throw new Error(`The project (id = ${projectId}) could not be found.`);
        }

        return {
          ...rawData.project,
          agreedWithCreativeVisibility: false,
        };
      } catch (err) {
        setError(err);
        if (err instanceof ProjectNotFound || (err instanceof GraphQLOperationFailed && err.type === 'NotFoundError')) {
          showFatalErrorMessage(new Error('message.get_project_has_failed'));
        }
        if (err instanceof GraphQLOperationFailed && err.type === 'UseUnpublishedProjectTypeError') {
          showFatalErrorMessage(new Error('message.project_uses_unpublished_project_type_error'));
        }
        throw err;
      }
    };

    // 発注後なのにdraft用の編集画面に遷移した場合
    const checkIfProjectIsNotDraftAfterSubmitted = (project: ExtendedProject): void | undefined => {
      // stepが指定されていない時（undefined時）は`step = format`とデフォルトで設定されているので、ここでもstep === undefined時は
      // stepがformatとして扱われるようにする
      if (project?.state !== 'draft' && DraftSteps.includes(step || 'format')) {
        const error = new Error('message.edit_project_has_failed');
        setError(error);
        showFatalErrorMessage(error);
        return;
      }
    };

    const checkIfProjectIsNotDraftForSubmittedScreens = (project: ExtendedProject): void | undefined => {
      if (project?.state === 'draft' && !DraftSteps.includes(step || 'format')) {
        const error = new Error('Tried to access a page that is only available for projects that have been submitted.');
        setError(error);
        showFatalErrorMessage(error);
        return;
      }
    };

    // オプションが1つも無いのにオプション入力画面に遷移した場合
    const checkIfOptionScreenHasValidData = (project: ExtendedProject): void | undefined => {
      if (
        step === 'option' &&
        project &&
        isProjectInstanceOfProjectDetailsFragment(project.projectType) &&
        (project?.projectType as ProjectDetailsFragment['projectType']).pricingMerchandiseOptions.length == 0
      ) {
        const error = new Error('message.edit_project_has_failed');
        setError(error);
        showFatalErrorMessage(error);
        return;
      }
    };

    // 提出後編集のページでローカルストレージに下書きがあればそれを編集対象にする
    const fetchDraftProjectFromLocalStorage = (project: ExtendedProject) => {
      const storageProject = JSON.parse(window.localStorage.getItem(`project_brief_${project.id}`) || '{}');
      if (project.versionId === storageProject.versionId) {
        return storageProject;
      }

      return project;
    };

    const project = await fetchProject();

    if (project) {
      checkIfProjectIsNotDraftAfterSubmitted(project);
      checkIfProjectIsNotDraftForSubmittedScreens(project);
      checkIfOptionScreenHasValidData(project);

      const updatedProject = fetchDraftProjectFromLocalStorage(project);

      setLastAccessedOrganization(project.organization);

      // 編集途中のプロジェクトで、アップロードされたbrief fileを取得し、ステートに入れる
      const briefFiles = await getBriefFiles(project);
      setBriefFilesState({
        ...briefFilesState,
        briefFiles,
        uploadingBriefFiles: Object.fromEntries(Object.keys(briefFiles).map((key) => [key, []])),
      });

      // 編集途中のプロジェクトで既にアップロードされている素材ファイルを取得し、ステートに入れる
      const assetFiles = await getAssetFiles(project.bucketId);
      setAssetFilesState({ ...assetFilesState, assetFiles });

      setAvailablePricingMerchandiseOptions(updatedProject);

      setProject(updatedProject);

      // 選択可能な納品期日の選択肢を取得し、ステートに入れる
      const { data: availableDeliveryDueDatesData } = await getAvailableDeliveryDueDates({
        variables: {
          projectId: project.id,
        },
      });

      if (availableDeliveryDueDatesData == null) {
        throw new Error('result.availableDeliveryDueDates is invalid');
      }

      setAvailableDeliveryDueDates(availableDeliveryDueDatesData.availableDeliveryDueDates);
    }
  };

  useEffect(() => {
    setProjectLoading(loading || withoutPricingDataLoading || isProjectForOptionScreenLoading);
  }, [setProjectLoading, loading, withoutPricingDataLoading, isProjectForOptionScreenLoading]);

  return {
    getProject,
    error,
  };
};
